import { useState, useCallback, useEffect } from "react";
import { updateFatigueSettings } from "../apiService";
import { FatigueSettings } from "../types";

export type FatigueCondition = {
  metric: string;
  comparison: "increases" | "decreases";
  midValue: number;
  highValue: number;
};

export type ComparisonPeriod = "daily" | "weekly" | "monthly";

export const useFatigueSettings = (
  accountId?: string,
  initialFatigueSettings?: FatigueSettings,
  onSettingsSaved?: () => void,
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [lastSavedSettings, setLastSavedSettings] = useState<FatigueSettings | undefined>(
    initialFatigueSettings,
  );
  const [comparisonPeriod, setComparisonPeriod] = useState<
    FatigueSettings["comparison_period"]
  >(lastSavedSettings?.comparison_period || "weekly");
  const [conditions, setConditions] = useState<FatigueSettings["conditions"]>(() => {
    if (lastSavedSettings?.conditions && lastSavedSettings.conditions.length > 0) {
      return lastSavedSettings.conditions;
    }
    // Default conditions when no initial settings exist
    return Array(3).fill({
      mid_fatigue: 15,
      high_fatigue: 35,
      operator: "increases",
      metric_id: "", // This will be populated with actual metrics in the dialog
    }) as FatigueSettings["conditions"];
  });

  // Reset to last saved settings, falling back to initial settings if no save has occurred
  const resetToLastSaved = useCallback(() => {
    if (lastSavedSettings) {
      setComparisonPeriod(lastSavedSettings.comparison_period);
      setConditions(lastSavedSettings.conditions);
    } else {
      // Reset to defaults if no saved settings
      setComparisonPeriod("weekly");
      setConditions(
        Array(3).fill({
          mid_fatigue: 15,
          high_fatigue: 35,
          operator: "increases",
          metric_id: "",
        }),
      );
    }
  }, [lastSavedSettings]);

  const handleSave = useCallback(async () => {
    if (!accountId) {
      console.error("Missing accountId");
      return;
    }

    try {
      const newSettings = {
        comparison_period: comparisonPeriod,
        conditions: conditions,
      };

      await updateFatigueSettings(accountId, newSettings);

      // Update last saved settings after successful save
      setLastSavedSettings(newSettings);
      setIsOpen(false);

      // Trigger retry callback after successful save
      onSettingsSaved?.();
    } catch (error) {
      console.error("Error saving fatigue settings:", error);
    }
  }, [accountId, comparisonPeriod, conditions, onSettingsSaved]);

  // Update states when accountId or initialFatigueSettings change
  useEffect(() => {
    if (initialFatigueSettings) {
      setLastSavedSettings(initialFatigueSettings);
      setComparisonPeriod(initialFatigueSettings.comparison_period);
      setConditions(initialFatigueSettings.conditions);
    } else {
      // Reset to defaults if no initial settings
      setLastSavedSettings(undefined);
      setComparisonPeriod("weekly");
      setConditions(
        Array(3).fill({
          mid_fatigue: 15,
          high_fatigue: 35,
          operator: "increases",
          metric_id: "",
        }),
      );
    }
  }, [accountId, initialFatigueSettings]);

  return {
    isOpen,
    setIsOpen,
    comparisonPeriod,
    setComparisonPeriod,
    conditions,
    setConditions,
    handleSave,
    resetToLastSaved,
  };
};
